import { useEffect, useState } from "react";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import CopyButton from "@/components/affiliate/CopyButton";
import { affGet } from "@/lib/affiliateApi";
import { C } from "@/lib/brand";

// Disclaimer library — every legally-vetted disclaimer the affiliate
// might need, with a note on when each applies. One-tap copy on each.
//
// API contract:
//   GET /affiliate/content/disclaimers →
//     { disclaimers: [{id, name, body, whenToUse}] }
export default function AssetsDisclaimers() {
  const [list, setList] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await affGet("/affiliate/content/disclaimers", { auth: true });
        const items = res?.items || res?.disclaimers || [];
        const normalized = items.map((d) => ({
          id: d.id || d._id || d.contentId,
          name: d.name || d.title || "Disclaimer",
          body: d.body || d.text || "",
          whenToUse: d.whenToUse || d.metadata?.whenToUse || d.subtitle,
        })).filter((d) => d.body);
        if (!cancelled) setList(normalized);
      } catch {
        if (!cancelled) setList([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (list === null) {
    return <div style={{ padding: 64, textAlign: "center" }}><Spinner size={28} /></div>;
  }

  return (
    <div style={{ maxWidth: 880, margin: "0 auto", padding: "40px 24px 80px" }}>
      <Eyebrow color={C.peachDeep}>★ QUICK REFERENCE</Eyebrow>
      <Headline before="DISCLAIMER" italic="library." size={48} className="mt-4" />
      <p style={{ marginTop: 14, fontSize: 15, color: C.warmGray, maxWidth: 640, lineHeight: 1.6 }}>
        Use these word-for-word. They're written for the platforms where they live, and they keep you on the right side of the rules.
      </p>
      <div data-testid="disclaimers-list" style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 16 }}>
        {list.length === 0 ? (
          <div data-testid="disclaimers-empty" style={{ padding: 24, background: C.bone, fontSize: 14, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
            No disclaimers published yet.
          </div>
        ) : (
          list.map((d) => (
            <article
              key={d.id}
              data-testid={`disclaimer-${d.id}`}
              style={{ background: C.pure, border: `1px solid ${C.warmGray3}`, padding: 22 }}
            >
              <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em" }}>
                  {d.name || "Disclaimer"}
                </h3>
                <CopyButton text={d.body} testId={`disclaimer-copy-${d.id}`} label="COPY" />
              </header>
              {d.whenToUse && (
                <p style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: C.peachDeep, letterSpacing: "0.04em" }}>
                  WHEN TO USE: <span style={{ color: C.warmGray, fontWeight: 400 }}>{d.whenToUse}</span>
                </p>
              )}
              <pre
                style={{
                  margin: "12px 0 0",
                  padding: 16,
                  background: C.bone,
                  color: C.ink,
                  fontSize: 13,
                  lineHeight: 1.65,
                  fontFamily: '"Instrument Serif", Georgia, serif',
                  fontStyle: "italic",
                  whiteSpace: "pre-wrap",
                }}
              >
                {d.body}
              </pre>
            </article>
          ))
        )}
      </div>
    </div>
  );
}
